import { useCallback } from 'react'
import { useFormikContext } from 'formik'
import {
  EscritoFormInitialValues,
  EscritosFormValues
} from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { FormField } from 'src/enums/Form'

interface UseClearFormSections {
  clearAdjuntosSection: () => void
  clearPartialAdjuntosSection: (values: EscritosFormValues) => void
  clearIntervinientesSection: () => void
  clearPartialIntervinientesSection: (values: EscritosFormValues) => void
  clearDatosSection: (partial?: boolean) => void
  clearOrganoDestinoFieldset: () => void
  clearOrganoDestinoFiedHidden: () => void
  clearTrasladoDeCopiasFieldset: () => void
  clearNIGFieldset: () => void
  clearProcedimientoFieldset: () => void
  clearPartialProcedimientoSection: (values: EscritosFormValues) => void
  clearOrganoOrigenFieldset: () => void
}

export default function useClearFormSections(): UseClearFormSections {
  const { setFieldValue } = useFormikContext()

  const clearOrganoDestinoFieldset = useCallback(() => {
    setFieldValue(FormField.COMUNIDAD_AUTONOMA, null, false)
    setFieldValue(FormField.PROVINCIA, null, false)
    setFieldValue(FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL, null, false)
    setFieldValue(FormField.ORGANO_DESTINO_ORGANO_JUDICIAL, null, false)
    setFieldValue(FormField.ORGANO_DESTINO, null, false)
    setFieldValue(FormField.ORGANO_DESTINO_ORDEN_JUDICIAL, null, false)
    setFieldValue(FormField.ORGANO_DESTINO_DESCRIPCION, '', false)
    setFieldValue(FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION, '', false)
    setFieldValue(FormField.ORGANO_DESTINO_ENVIAR_JUZGADO_GUARDIA, false, false)
    setFieldValue(FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL, null, false)
  }, [setFieldValue])

  const clearOrganoDestinoFiedHidden = useCallback(() => {
    // setFieldValue(FormField.ORGANO_DESTINO, null, false)
    setFieldValue(FormField.ORGANO_DESTINO_DESCRIPCION, '', false)
    setFieldValue(FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION, '', false)
  }, [setFieldValue])

  const clearTrasladoDeCopiasFieldset = useCallback(() => {
    setFieldValue(FormField.TRASLADO_COPIAS, [], false)
  }, [setFieldValue])

  const clearIntervinientesSection = useCallback(() => {
    setFieldValue(FormField.INTERVINIENTES, [])
  }, [setFieldValue])

  const clearPartialIntervinientesSection = useCallback(
    (values: EscritosFormValues) => {
      setFieldValue(
        FormField.INTERVINIENTES,
        values[FormField.INTERVINIENTES]?.map(interviniente => ({
          ...interviniente,
          [FormField.INTERVINIENTE_INTERVENCION_TIPO]: null,
          validated: false
        })),
        false
      )
    },
    [setFieldValue]
  )

  const clearPartialProcedimientoSection = useCallback(
    (values: EscritosFormValues) => {
      setFieldValue(
        FormField.PROCEDIMIENTO,
        {
          ...values[FormField.PROCEDIMIENTO],
          [FormField.PROCEDIMIENTO_TIPO_FORM]: null,
          [FormField.PROCEDIMIENTO_TIPO_ORIGEN_FORM]: null
        },
        false
      )

      setFieldValue(FormField.PROCEDIMIENTO_DESCRIPCION, '', false)
      setFieldValue(FormField.PROCEDIMIENTO_ORIGEN, '', false)
    },
    [setFieldValue]
  )

  const clearDatosSection = useCallback(
    (partial = false) => {
      if (!partial) {
        setFieldValue(FormField.DATOS_OBSERVACIONES, '', false)
        setFieldValue(FormField.DATOS_URGENTE, false, false)
      }
      setFieldValue(FormField.DATOS_ORIGEN_DEUDA, '', false)
      setFieldValue(FormField.DATOS_FUNDAMENTOS_DERECHO, '', false)
      setFieldValue(FormField.DATOS_PETICION, '', false)
      setFieldValue(FormField.DATOS_PETICION_COMPLEMENTARIA, '', false)
      setFieldValue(FormField.DATOS_RECLAMACION_CONSUMIDOR, false, false)
      setFieldValue(FormField.DATOS_RECLAMACION_COMUNIDAD, false, false)
      setFieldValue(FormField.DATOS_DEUDA_PARTIDO_JUDICIAL, false, false)
      setFieldValue(FormField.DATOS_RECONVENCION, 'No', false)
      setFieldValue(FormField.DATOS_TIPO_CUANTIA, null, false)
      setFieldValue(FormField.DATOS_CUANTIA_RECONVENCION, null, false)

      setFieldValue(FormField.DATOS_MATERIA, null, false)
      setFieldValue(FormField.DATOS_FECHA_ACUERDO, null, false)
      setFieldValue(FormField.DATOS_OBJETO_RECURSO, '', false)
      setFieldValue(FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN, '', false)
      setFieldValue(FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN, '', false)
      setFieldValue(FormField.DATOS_ORGANISMO, '', false)
      setFieldValue(FormField.DATOS_DELITO, null, false)
      setFieldValue(FormField.DATOS_LOCALIDAD, '', false)
      setFieldValue(FormField.DATOS_FECHA, null, false)
      setFieldValue(FormField.DATOS_HORA, null, false)
      setFieldValue(FormField.DATOS_MEDIDA_CAUTELAR_PREVIA, false, false)
      setFieldValue(FormField.DATOS_CAUTELARISIMA, false, false)
      setFieldValue(FormField.DATOS_MEDIDA_CAUTELAR_COETANEA, false, false)
      setFieldValue(FormField.DATOS_CAUSA_CON_DETENIDO, false, false)
      setFieldValue(FormField.DATOS_CAUSA_CON_PRESO, false, false)
      setFieldValue(FormField.DATOS_VIOLENCIA_GENERO, false, false)
      setFieldValue(FormField.DATOS_CUANTIA_INTERESES, '0', false)
      setFieldValue(FormField.DATOS_CUANTIA_COSTAS, '0', false)
      setFieldValue(FormField.DATOS_ORGANISMO_ORIGEN, null, false)
    },
    [setFieldValue]
  )

  const clearAdjuntosSection = useCallback(() => {
    setFieldValue(
      FormField.DOCUMENTO_PRINCIPAL,
      EscritoFormInitialValues[FormField.DOCUMENTO_PRINCIPAL]
    )
    setFieldValue(
      FormField.DOCUMENTO_PRINCIPAL_ARCHIVO,
      EscritoFormInitialValues[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO]
    )
    setFieldValue(FormField.EXCESO_CABIDA, false)
    setFieldValue(FormField.DOCUMENTOS_ANEXOS, [])
    setFieldValue(FormField.DOCUMENTOS_ANEXOS_ARCHIVO, [])
  }, [setFieldValue])

  const clearPartialAdjuntosSection = useCallback(
    (values: EscritosFormValues) => {
      setFieldValue(
        FormField.DOCUMENTO_PRINCIPAL,
        values[FormField.DOCUMENTO_PRINCIPAL]?.map(d => ({ ...d, subtipo: null }))
      )

      setFieldValue(
        FormField.DOCUMENTOS_ANEXOS,
        values[FormField.DOCUMENTOS_ANEXOS]?.map((d: any) => ({ ...d, subtipo: null }))
      )
    },
    [setFieldValue]
  )

  const clearNIGFieldset = useCallback(() => {
    setFieldValue(FormField.NIG_FULL_NUMBER, '', false)
    setFieldValue(FormField.NIG_1, '', false)
    setFieldValue(FormField.NIG_2, '', false)
    setFieldValue(FormField.NIG_3, '', false)
    setFieldValue(FormField.NIG_4, '', false)
    setFieldValue(FormField.NIG_5, '', false)
  }, [setFieldValue])

  const clearProcedimientoFieldset = useCallback(() => {
    setFieldValue(FormField.PROCEDIMIENTO_DESCRIPCION, '', false)
    setFieldValue(FormField.PROCEDIMIENTO_REFERENCIA, '', false)
  }, [setFieldValue])

  const clearOrganoOrigenFieldset = useCallback(() => {
    setFieldValue(FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA, null, false)
    setFieldValue(FormField.ORGANO_ORIGEN_PROVINCIA, null, false)
    setFieldValue(FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL, null, false)
    setFieldValue(FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL, null, false)
    setFieldValue(FormField.ORGANO_ORIGEN, null, false)
    setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL, null, false)
    setFieldValue(FormField.ORGANO_ORIGEN_DESCRIPCION, '', false)
    setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_DESCRIPCION, '', false)
    setFieldValue(FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN, null, false)
    setFieldValue(FormField.ORGANO_ORIGEN_NUMERO_EXPEDIENTE, '', false)
    setFieldValue(FormField.ORGANO_ORIGEN_YEAR_EXPEDIENTE, '', false)
  }, [setFieldValue])

  return {
    clearDatosSection,
    clearAdjuntosSection,
    clearPartialAdjuntosSection,
    clearIntervinientesSection,
    clearPartialIntervinientesSection,
    clearOrganoDestinoFieldset,
    clearOrganoDestinoFiedHidden,
    clearTrasladoDeCopiasFieldset,
    clearNIGFieldset,
    clearProcedimientoFieldset,
    clearPartialProcedimientoSection,
    clearOrganoOrigenFieldset
  }
}
